import './style.scss'
import { Button, Checkbox, Input, message, Modal, QRCode, Select } from 'antd'
import { FC, memo, Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Api from '@/api/account'
import * as HomeApi from '@/api/home'
import { IProduct, UserStore } from '@/global-states'
import { eventTracking } from '@/libs/util'
import { AlipayOutlined, RightOutlined, WechatOutlined } from '@ant-design/icons'
import CheckLogin from '../CheckLogin'
import UserModalHeader from '../UserModalHeader'

interface IProps {
  product?: IProduct
  open: boolean
  hiddenInviteCode?: boolean
  onCancel?: () => void
  onSuccess?: () => void
}

const PayModal: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['pay_modal'])
  const { product, open, hiddenInviteCode, onCancel } = props
  const { userPackage } = UserStore
  const [payMethod, setPayMethod] = useState(1)
  const [loading, setLoading] = useState(false)
  const [payUrl, setPayUrl] = useState('')
  const [checked, setChecked] = useState(true)
  const payTimer = useRef<any>()
  const [affiliateCode, setAffiliateCode] = useState('')
  const [couponList, setCouponList] = useState<any[]>([])
  const [coupon, setCoupon] = useState<any>()
  const modalRoot = document.createElement('div')
  modalRoot.style.position = 'absolute'
  modalRoot.style.zIndex = '1001'
  modalRoot.style.top = '0'
  modalRoot.style.left = '0'

  useEffect(() => {
    setPayMethod(1)
    setPayUrl('')
    clearPayTimer()
    if (open) {
      getClaimedCoupons()
      const code = sessionStorage.getItem('code')
      if (code) {
        setAffiliateCode(code)
      } else if (userPackage?.invitor_aff_code) {
        setAffiliateCode(userPackage.invitor_aff_code)
      } else {
        setAffiliateCode('')
      }
    }
  }, [open])

  const getClaimedCoupons = async () => {
    const res = await HomeApi.getClaimedCoupons({
      status: 2
    })
    const list = res?.list || []
    const selectOptions = [
      {
        label: translate('pay_modal.no_coupon'),
        value: -1,
        id: -1,
        deduct_amount: 0
      },
      ...(list.map((l: any) => ({
        ...l,
        label: l.title,
        value: l.id
      })) || [])
    ]
    setCouponList(selectOptions)
    const maxItem = selectOptions.reduce((max: any, item: any) => (item.deduct_amount > max.deduct_amount ? item : max))
    if (maxItem) {
      setCoupon(maxItem)
    }
  }

  const changePayMethod = (method: number) => {
    setPayMethod(method)
  }

  const toPay = async () => {
    setLoading(true)

    try {
      const params: any = {
        package_id: product?.id,
        client_type: 1,
        affiliate_code: affiliateCode
      }
      if (coupon?.id && coupon?.id !== -1) {
        params.coupon_id = coupon.id
      }
      const { pay_order_id, completed } = await Api.orderCreate(params)

      if (completed) {
        message.success(translate('pay_modal.payment_success'))
        onCancel?.()
        UserStore.updatePayStatus()
        return
      }

      const buyParams = {
        orderId: pay_order_id,
        payType: payMethod === 0 ? 'ALIPAY' : 'WECHATPAY'
      }

      eventTracking('PayNow', buyParams)

      const buyRes = await Api.paymentInit(buyParams)
      UserStore.updatePayStatus()

      if (payMethod === 1) {
        setPayUrl(buyRes?.pay_list.wechatpayNative?.code_url)
        paymentCheck(pay_order_id)
        payTimer.current = setInterval(() => {
          paymentCheck(pay_order_id)
        }, 3000)
      } else {
        location.href = buyRes?.pay_list?.alipay
      }
    } catch (e: any) {
      message.destroy()
      if (e.code === 14) {
        message.error(translate('pay_modal.package_expired'))
      } else {
        message.error(e.message)
      }
    } finally {
      setLoading(false)
    }
  }

  const paymentCheck = async (orderId: string) => {
    const res = await Api.paymentCheck({
      orderId
    })
    if (res?.is_pay === 1) {
      message.success(translate('pay_modal.payment_success'))
      onCancel?.()
      UserStore.updatePayStatus()
    }
  }

  const clearPayTimer = () => {
    if (payTimer.current) {
      clearInterval(payTimer.current)
      payTimer.current = undefined
    }
  }

  return (
    <Suspense>
      <Modal className="pay-modal" footer={null} width={660} onCancel={() => onCancel?.()} open={open}>
        <div className="pay-content">
          <UserModalHeader />
          <div className="pay-content-main">
            {!payUrl ? (
              <>
                <div className="pay-methods">
                  <div className="name">{translate('pay_modal.payment_method')}</div>
                  <div className="pay-list">
                    <div
                      className={`item wechat ${payMethod === 1 ? 'actived' : ''}`}
                      onClick={changePayMethod.bind(this, 1)}
                    >
                      <WechatOutlined />
                      <div>{translate('pay_modal.wechat_pay')}</div>
                    </div>
                    <div
                      className={`item alipay ${payMethod === 0 ? 'actived' : ''}`}
                      onClick={changePayMethod.bind(this, 0)}
                    >
                      <AlipayOutlined />
                      <div>{translate('pay_modal.alipay')}</div>
                    </div>
                  </div>
                </div>

                {!!product?.details?.length && (
                  <div className="tips">
                    <div className="name">{translate('pay_modal.purchase_benefits')}</div>
                    <ul>
                      {product.details.map((d, index) => (
                        <li key={index}>{d}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {!hiddenInviteCode && (
                  <div className="invite-code">
                    <div className="name">
                      <label>{translate('pay_modal.enter_invite_code')}</label>
                      <span className="text">
                        {translate('pay_modal.invite_reward', {
                          points: product?.duration_type === 1 && product.units === 1 ? '5000' : '60000'
                        })}
                      </span>
                    </div>
                    <Input
                      value={affiliateCode}
                      placeholder={translate('pay_modal.enter_invite_code')}
                      onChange={(e) => setAffiliateCode(e.target.value)}
                    />
                  </div>
                )}

                <div className="pay-coupon">
                  <div className="name">{translate('pay_modal.coupon')}</div>
                  <div className="detail">
                    {coupon?.id === -1 ? (
                      <>
                        <label className="unuse">{translate('pay_modal.no_coupon')}</label>
                      </>
                    ) : (
                      <>
                        <label>- ¥ {coupon?.deduct_amount / 100}</label>
                        <label className="desc">{coupon?.title}</label>
                      </>
                    )}

                    <RightOutlined className="arrow-right" />

                    <Select
                      className="select-coupon"
                      value={coupon?.id}
                      options={couponList}
                      onChange={(_, c) => {
                        setCoupon(c)
                      }}
                      defaultValue={-1}
                    ></Select>
                  </div>
                </div>

                <div className="plan">
                  <div className="name">{translate('pay_modal.amount_due')}</div>
                  {coupon && coupon.deduct_amount > 0 ? (
                    <div className="money">
                      {(product?.total_price || 0) > coupon.deduct_amount ? (
                        <label className="gradient">
                          ￥{((product?.total_price || 0) - coupon.deduct_amount) / 100}
                        </label>
                      ) : (
                        <label className="gradient">￥0</label>
                      )}
                      <label className="origin-price">￥{(product?.total_price || 0) / 100}</label>
                    </div>
                  ) : (
                    <div className="money gradient">￥{(product?.total_price || 0) / 100}</div>
                  )}
                </div>

                <div className="btn">
                  <CheckLogin>
                    <Button disabled={!checked} loading={loading} type="primary" onClick={toPay}>
                      {translate('pay_modal.pay_now')}
                    </Button>
                  </CheckLogin>
                </div>
                <div className="checkbox">
                  <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)}></Checkbox>
                  <div>
                    {translate('pay_modal.agreement_text')}
                    <a className="gradient" href="/service_agreement.html" target="_blank">
                      {translate('pay_modal.service_agreement')}
                    </a>
                  </div>
                </div>
              </>
            ) : (
              <div className="pay-qrcode">
                <div className="pay-desc">
                  <div>{translate('pay_modal.scan_to_pay')}</div>
                  {coupon && coupon.deduct_amount > 0 ? (
                    <>
                      {(product?.total_price || 0) > coupon.deduct_amount ? (
                        <div className="mount gradient">
                          {((product?.total_price || 0) - coupon.deduct_amount) / 100}
                        </div>
                      ) : (
                        <div className="mount gradient">{(product?.total_price || 0) / 100}</div>
                      )}
                    </>
                  ) : (
                    <div className="mount gradient">{(product?.total_price || 0) / 100}</div>
                  )}
                </div>
                <QRCode value={payUrl} />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </Suspense>
  )
}

export default memo(PayModal)
